import { useLazyQuery } from '@apollo/client';
import { AllSuperintendentId, getUnitOfMeasure, useAppUser, usePlanning } from '@bechtel/client-common';
import { UNIT_OF_MEASURE } from '@bechtel/client-common/src/types';
import { GET_THREE_WEEK_LOOK_AHEAD_DATA } from '@bechtel/client-gql';
import { Box } from '@bechtel/gds';
import {
  CrewMember,
  CrewMemberWeeklyLookAheadStatsInput,
  PersonaType,
  WeeklyLookAheadStatsByPersona,
} from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { FC, useEffect, useState } from 'react';
import { ChildContainerStyled } from '../styled';
import { CrewTree } from './crew-tree';
import { ThreeWeekLookAheadHeader } from './header';
import { ThreeWLAWeekHeader } from './week-header';
interface ThreeWeekLookAheadProps {
  selectedCrewMember: CrewMember | null;
}
export const ThreeWeekLookAhead: FC<ThreeWeekLookAheadProps> = ({ selectedCrewMember }) => {
  const { user } = useAppUser();
  const unitOfMeasureFromUser = getUnitOfMeasure(user);
  const { selectedSuperintendent, selectedGeneralForeman } = usePlanning();
  const [unitOfMeasure, setUnitOfMeasure] = useState<UNIT_OF_MEASURE>(unitOfMeasureFromUser);
  const [getWeeklyLookAheadStatsByPersona, result] = useLazyQuery<
    { weeklyLookAheadStatsByPersona: WeeklyLookAheadStatsByPersona[] },
    { input: CrewMemberWeeklyLookAheadStatsInput }
  >(GET_THREE_WEEK_LOOK_AHEAD_DATA, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (!selectedSuperintendent) return;
    const input: CrewMemberWeeklyLookAheadStatsInput = {
      numNextWeeks: 2,
      numPreviousWeeks: 1,
      isAllSuperintendents: false,
    };
    if (selectedSuperintendent.id === AllSuperintendentId) {
      input.isAllSuperintendents = true;
    } else {
      input.crewMember = {
        persona: PersonaType.SUPERINTENDENT,
        crewMemberId: selectedSuperintendent.id,
      };
    }
    getWeeklyLookAheadStatsByPersona({
      variables: {
        input: input,
      },
    });
  }, [selectedSuperintendent, getWeeklyLookAheadStatsByPersona]);

  useEffect(() => {
    setUnitOfMeasure(unitOfMeasureFromUser);
  }, [unitOfMeasureFromUser]);

  return (
    <ChildContainerStyled style={{ height: 'max-content' }}>
      <ThreeWeekLookAheadHeader
        unitOfMeasure={unitOfMeasure}
        setUnitOfMeasure={setUnitOfMeasure}
        disabled={!selectedCrewMember}
      />
      <ThreeWLAWeekHeader />
      {(!selectedCrewMember || !selectedCrewMember.persona) && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          py={{ sm: '900' }}
          flexGrow={{ sm: 1 }}
          data-testid="no-persona-selected"
        >
          <Trans>No user or persona selected</Trans>
        </Box>
      )}
      {selectedCrewMember && (
        <CrewTree
          result={result}
          unitOfMeasure={unitOfMeasure}
          selectedCrewMember={selectedCrewMember}
          selectedSuperintendent={selectedSuperintendent}
          selectedGeneralForeman={selectedGeneralForeman}
          depth={0}
          user={user}
        />
      )}
    </ChildContainerStyled>
  );
};
